import {
	QUESTION_TYPE_ID,
	RFX_ANSWER_CHOICE_TYPE,
	RFX_QUESTION_TYPE,
} from "./constants";
import {
	CorporateDocumentsOtherAnswer,
	ReturnableScheduleFileUpload,
	RfxAnswers,
	TableRowAnswer,
} from "./types/returnable-schedule-answer.type";
import {
	RfxQuestion,
	RfxReturnableSchedule,
} from "../types/returnable-schedule-question.type";
import { isAnswerOfType } from "./submission-helpers";
import { logErrorIfDebug } from "utils/logging";
import { RfxQuoteAnswers, RfxQuotes } from "./types/quote.type";
import {
	isNotNullOrUndefined,
	isNullOrUndefined,
} from "newui/utils/objectUtil";
import { removePrefixFromKeys } from "utils/helpers";

const getValueZeroOrMore = (value) => {
	const numericValue = parseFloat(value);
	return numericValue >= 0 ? numericValue : null;
};

export const initialiseQuoteValues = (
	quotes: RfxQuotes,
	quoteAnswers: RfxQuoteAnswers
) => {
	const quoteValues = {};
	quotes?.forEach((quote) => {
		quote.items?.forEach((item) => {
			const quoteAnswer = [...(quoteAnswers ?? [])]
				.sort((a, b) => b.revision - a.revision)
				.find((answer) => answer.rfqQuoteItemId === item.id);

			quoteValues[`quote-${item.id}`] = {
				quantity: getValueZeroOrMore(quoteAnswer?.answer?.value?.quantity),
				rate: getValueZeroOrMore(quoteAnswer?.answer?.value?.rate),
				unit: item.unit?.id || null,
			};
		});
	});
	return { quote_answer: { ...quoteValues } };
};

const getDefaultValueForQuestionType = (
	question: RfxQuestion,
	hasPastProjects: boolean,
	hasResources: boolean
) => {
	switch (question.type) {
		case QUESTION_TYPE_ID.CORPORATE_DOCUMENTS:
		case QUESTION_TYPE_ID.FILE_UPLOAD:
		case QUESTION_TYPE_ID.MULTIPLE_CHOICE:
		case QUESTION_TYPE_ID.TABLE:
			return [];

		case QUESTION_TYPE_ID.KEY_PERSONNEL:
			return hasResources ? [] : "";

		case QUESTION_TYPE_ID.PROJECT_EXPERIENCE:
			return hasPastProjects ? [] : "";
		case QUESTION_TYPE_ID.TABLE_ROWS: {
			const columns = [];
			// generate correct number of columns for default
			// @ts-ignore We are a table based question type
			question.fields.table.forEach((element) => {
				columns.push({ id: "", value: "" });
			});

			return [columns] as TableRowAnswer;
		}
		default:
			return null;
	}
};

export const initialiseReturnableSchedule = (
	returnableSchedule: RfxReturnableSchedule,
	rfxAnswers: RfxAnswers,
	hasDocuments: boolean,
	hasPastProjects: boolean,
	hasResources: boolean,
	isClientView: boolean
) => {
	const questionValues = {};

	returnableSchedule?.forEach((section) => {
		section.questionnaire?.forEach((question) => {
			const answerData = rfxAnswers?.find(
				(answer) => answer.rfqQuestionId === question.id
			);

			const answer = answerData?.answer;

			const answerHasAValue = isNotNullOrUndefined(answer?.value);
			const isAnswerAnArray = Array.isArray(answer?.value);
			const answerWithValueAndArray = answerHasAValue && isAnswerAnArray;
			let fieldValue;

			try {
				if (isNullOrUndefined(answer)) {
					const defaultValue = getDefaultValueForQuestionType(
						question,
						hasPastProjects,
						hasResources
					);
					questionValues[`question-${question.id}`] = defaultValue;

					return;
				}

				switch (answer?.type) {
					/**
					 * GENERAL FIELDS
					 */
					case RFX_QUESTION_TYPE.CHECKBOX: {
						fieldValue = answerWithValueAndArray
							? answer.value.map((ans) => ans.toString())
							: [];
						break;
					}
					case RFX_QUESTION_TYPE.DATE:
					case RFX_QUESTION_TYPE.RADIO:
					case RFX_QUESTION_TYPE.SELECT:
					case RFX_QUESTION_TYPE.TEXT:
					case RFX_QUESTION_TYPE.TEXTAREA: {
						fieldValue = answerHasAValue ? answer.value.toString() : null;
						break;
					}
					case RFX_QUESTION_TYPE.FILE: {
						fieldValue = answerWithValueAndArray
							? answer.value.map((file) => ({
									file_name: file?.fileName || " - ",
									file_size: file?.fileSize || null,
									file_path: file?.filePath || null,
								}))
							: [];
						break;
					}
					case RFX_QUESTION_TYPE.TABLE: {
						const fieldValues = answerWithValueAndArray
							? answer.value
							: // @ts-ignore - this is a table type
								question?.fields?.table[1]?.values;

						// do not map to fieldValue for this question
						fieldValue = fieldValues;
						break;
					}
					case RFX_QUESTION_TYPE.TABLE_ROWS: {
						fieldValue = answerWithValueAndArray
							? answer.value
							: ([[{ id: "", value: "" }]] as TableRowAnswer);
						break;
					}

					/**
					 * SMART FIELDS
					 */
					case RFX_QUESTION_TYPE.CORPORATE_DOCUMENTS: {
						if (answer.chosen_type === RFX_ANSWER_CHOICE_TYPE.SELECT) {
							// For selected documents - extract only id
							fieldValue = answerWithValueAndArray
								? answer.value.map((item) => item)
								: [];
						} else if (answer.chosen_type === RFX_ANSWER_CHOICE_TYPE.OTHER) {
							const otherAnswer = answer as CorporateDocumentsOtherAnswer;
							fieldValue =
								isNotNullOrUndefined(otherAnswer.value) &&
								Array.isArray(otherAnswer.value)
									? otherAnswer.value.map((file) => ({
											file_name: file?.fileName || " - ",
											file_size: file?.fileSize || null,
											file_path: file?.filePath || null,
										}))
									: [];
						}
						break;
					}
					case RFX_QUESTION_TYPE.KEY_PERSONNEL: {
						if (isAnswerOfType(answer, RFX_QUESTION_TYPE.KEY_PERSONNEL)) {
							fieldValue = getKeyPersonnelOrProjectExperienceValue(
								isClientView || hasResources,
								answer.value,
								answer.chosen_type
							);
						}
						break;
					}
					case RFX_QUESTION_TYPE.PROJECT_EXPERIENCE: {
						if (isAnswerOfType(answer, RFX_QUESTION_TYPE.PROJECT_EXPERIENCE)) {
							fieldValue = getKeyPersonnelOrProjectExperienceValue(
								isClientView || hasPastProjects,
								answer.value,
								answer.chosen_type
							);
						}
						break;
					}
					default: {
						fieldValue = "";
						break;
					}
				}
				questionValues[`question-${question.id}`] = fieldValue;

				if (question.hasComment) {
					questionValues[`question-${question.id}-comment`] =
						answerData.comment;
				}
			} catch (exception) {
				logErrorIfDebug("Error loading answer to question", [
					question,
					answer,
					exception,
				]);
			}
		});
	});
	return {
		question_answer: { ...questionValues },
	};
};

const getKeyPersonnelOrProjectExperienceValue = (
	hasItemsToPickFrom: boolean,
	answerValue: number[],
	chosenType: string | undefined
) => {
	if (hasItemsToPickFrom && chosenType !== "other") {
		return answerValue
			? typeof answerValue === "string"
				? []
				: answerValue.map((ans) => ans).filter((ans) => ans !== undefined)
			: [];
	} else {
		/**
		 * if supplier but no projects or resources show textarea
		 */
		return answerValue?.[0] || [];
	}
};

const processFileUploadPayload = (value) => {
	if (!value || !Array.isArray(value)) {
		return [];
	}

	return {
		chosen_type: "other",
		additional: value.map((file) => {
			return JSON.stringify(
				new ReturnableScheduleFileUpload(
					file.file_name,
					file.file_path,
					file.file_size
				)
			);
		}),
	};
};

export const generateSaveSubmissionPayload = (
	values: any,
	returnableList,
	isGuest
) => {
	const returnablePayload = removePrefixFromKeys(
		{ ...values?.question_answer },
		"question-"
	);
	const quotePayload = removePrefixFromKeys(
		{ ...values?.quote_answer },
		"quote-"
	);
	const quoteFiles = values?.quote_files;
	const returnableScheduleFileResponses =
		values?.returnable_schedule_file_responses;

	const comments = [];

	returnableList?.forEach((section) => {
		section.questionnaire?.forEach((question) => {
			try {
				const value = returnablePayload[question.id];
				const commentValue = returnablePayload[`${question.id}-comment`];

				if (commentValue && commentValue.length > 0) {
					comments.push({ id: question.id, comment: commentValue });
				}

				// if emptying an array value, we want to remove it in the DB
				if (Array.isArray(value) && value?.length === 0) {
					returnablePayload[question.id] = { isRemove: 1 };
					return;
				}

				// logic based upon question type
				switch (question?.fields?.type) {
					case "corporate_documents": {
						// check if we have uploaded a file rather than selecting one (always the case for guests)
						if (Array.isArray(value) && value[0]?.file_size) {
							returnablePayload[question.id] = processFileUploadPayload(value);
							return;
						}

						// Type SAFE --Filter only number values from the array
						const selectedIds = Array.isArray(value)
							? value.filter(
									(item) =>
										typeof item === "number" ||
										// @ts-ignore - item is a string
										(typeof item === "string" && !isNaN(item))
								)
							: null;
						returnablePayload[question.id] = selectedIds;
						return;
					}
					case "file": {
						returnablePayload[question.id] = processFileUploadPayload(value);
						return;
					}
					case "key_personnel":
					case "project_experience": {
						if (Array.isArray(value)) {
							return;
						} else if (isGuest) {
							returnablePayload[question.id] = [value];
							return;
						}

						returnablePayload[question.id] = {
							chosen_type: "other",
							additional: value || [],
						};

						break;
					}
					case "text":
					case "textarea": {
						if (value === "") {
							returnablePayload[question.id] = "<p></p>";
						}
						break;
					}
					case "table": {
						// need to ensure we populate all rows, so we don't get an index mismatch on reload
						const rowCount = question?.fields?.table?.[0]?.values?.length ?? 0;
						const values = [];
						for (let index = 0; index < rowCount; index++) {
							if (value.length > index) {
								values.push(value[index] ?? "");
							} else {
								values.push("");
							}
						}
						returnablePayload[question.id] = values;
						break;
					}
					case "table-to-add-rows": {
						returnablePayload[question.id] = value;
						break;
					}
					default:
						returnablePayload[question.id] = value;
						break;
				}
			} catch (exception) {
				const message = `Failed to get data for Question ${question.sort_order} in Section ${section.sort_order} '${section.name}' - please try again or contact Procuracon for support.`;
				throw Error(message);
			}
		});
	});

	for (const quote in quotePayload) {
		const quoteValue = quotePayload[quote];
		quotePayload[quote].rate = getValueZeroOrMore(quoteValue.rate);
		quotePayload[quote].quantity = getValueZeroOrMore(quoteValue.quantity);
		quotePayload[quote].unit = quoteValue.unit || 1;
	}

	const quoteResponse = quoteFiles
		? { quote_files: quoteFiles }
		: { quote_answer: quotePayload };
	const returnableResponse = returnableScheduleFileResponses
		? { returnable_schedule_file_responses: returnableScheduleFileResponses }
		: { question_answer: returnablePayload };

	return {
		...returnableResponse,
		...quoteResponse,
		comments: comments,
	};
};
